import { Stack } from '@mui/material'
import { appUserColsMap, AppUserListColumns } from 'components/core/app-user/C1AppUsersList'
import { C1TableRedux } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { SelectOneAppHook } from 'components/core/search/apps'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { QUERIES } from 'components/pages/explore/query/Query'
import { StructuredSearchStyles } from 'components/pages/explore/query/styles'
import { AppValue } from 'lib/actions/apps'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user/index'
import { useInitialFilters } from 'lib/search'
import { useRouter } from 'next/router'
import { AppUsersWithoutResponsiblePartyRequest } from 'pbts/c1/api/app/v1/app_user'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { getAppUsersWithoutResponsibleParty } from 'redux_toolkit/app-users'

const AccountsWithoutOwnerReport: FC<{appId: string|undefined}> = ({ appId }) => {
    const [page, setPage] = useState<number>(0)
    const { data, isError, isLoading, getPage, canLoadMore, error } =
        getAppUsersWithoutResponsibleParty.useAppUsersWithoutResponsibleParty(
            AppUsersWithoutResponsiblePartyRequest.create({ expandMask: { paths: ['*'] }, appId }),
            { },
        )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AppUsersWithoutOwner)

    const table = useMemo(
        () => (
            <C1TableRedux<AppUserValue>
                data={data}
                cols={[
                    appUserColsMap[AppUserListColumns.account],
                    appUserColsMap[AppUserListColumns.app],
                    appUserColsMap[AppUserListColumns.status],
                    appUserColsMap[AppUserListColumns.accountType],
                    appUserColsMap[AppUserListColumns.user],
                    appUserColsMap[AppUserListColumns.roles],
                    appUserColsMap[AppUserListColumns.lastLogin],
                    appUserColsMap[AppUserListColumns.actions],
                ]}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                onPageChange={onPageChange}
                error={error}
                page={page}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    return <List<UserValue> table={table} />
}

export const AccountsWithoutOwner: FC = () => {
    const router = useRouter()
    const initialFilter = useInitialFilters(AppUsersWithoutResponsiblePartyRequest)

    const [selectedAppId, setSelectedAppId] = useState<string | undefined>(initialFilter.appId)

    const onSelect = useCallback((app:AppValue|null): void => {
        setSelectedAppId(app?.id)
        router.query.appId = app?.id
        router.replace({ pathname: router.pathname, query: router.query }, undefined, { shallow: true })
    }, [router])

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.APP_USERS_WITHOUT_OWNER,
            request: {
                oneofKind: 'appUsersWithoutResponsiblePartyRequest',
                appUsersWithoutResponsiblePartyRequest: AppUsersWithoutResponsiblePartyRequest.create({ expandMask: { paths: ['*'] }, appId: selectedAppId }),
            },
        })
    }, [selectedAppId])

    return <>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-end" pr={1}>
            <SelectOneAppHook
                onSelected={onSelect}
                initAppId={initialFilter.appId}
                sx={sxc(StructuredSearchStyles.selectApp, StructuredSearchStyles.filterInputs)}
            />
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Stack>
        <AccountsWithoutOwnerReport appId={selectedAppId} />
    </>
}
